import { useCallback, useEffect, useState } from 'react';
import { deleteAudioContent, listAudioContent, renameAudioContent } from '../api/audioApi';
import { AudioContentItem } from '../types';
import { AUDIO_CREATED_EVENT } from './useLibrary';

export interface DocumentAudioState {
  items: AudioContentItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  rename: (id: number, title: string) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

// Audio generated from a specific source document. The gateway filters by
// source document id, so we don't pull the whole library and match client-side.
export function useDocumentAudio(documentId: number | null): DocumentAudioState {
  const [items, setItems] = useState<AudioContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (documentId == null) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setItems(await listAudioContent({ documentId }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load audio');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onCreated = () => load();
    window.addEventListener(AUDIO_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(AUDIO_CREATED_EVENT, onCreated);
  }, [load]);

  const rename = useCallback(async (id: number, title: string) => {
    await renameAudioContent(id, title);
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, title } : it)));
  }, []);

  const remove = useCallback(async (id: number) => {
    await deleteAudioContent(id);
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  return { items, loading, error, refresh: load, rename, remove };
}
